import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FileText, Download, Eye, Scale, CheckCircle, Calendar, Building, Award } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';

// Court document data for 5 cases
const courtDocuments = [
  {
    id: 'HC-2024-003847',
    clientName: 'Mohammed Al Ali',
    clientNameAr: 'محمد العلي',
    amount: '$847,500',
    company: 'Capital Market FX Limited',
    court: 'High Court of Justice, Queen\'s Bench Division, Commercial Court',
    courtAr: 'المحكمة العليا - الشعبة التجارية',
    date: 'March 2024',
    dateAr: 'مارس 2024',
    judge: 'The Honourable Mr Justice Williams',
    judgment: 'IN FAVOUR OF CLAIMANT'
  },
  {
    id: 'BC-2024-001592',
    clientName: 'Sarah Ahmed',
    clientNameAr: 'سارة أحمد',
    amount: '$625,000',
    company: 'Binary Options Pro Ltd',
    court: 'High Court of Justice, Business and Property Courts, Commercial Court',
    courtAr: 'المحكمة التجارية البريطانية',
    date: 'January 2024',
    dateAr: 'يناير 2024',
    judge: 'The Honourable Mrs Justice Chen',
    judgment: 'IN FAVOUR OF CLAIMANT'
  },
  {
    id: 'HC-2023-001203',
    clientName: 'Ahmed Al Khatib',
    clientNameAr: 'أحمد الخطيب',
    amount: '$1,240,000',
    company: 'Crypto Trade Global Inc',
    court: 'High Court of Justice, Queen\'s Bench Division',
    courtAr: 'المحكمة العليا البريطانية',
    date: 'November 2023',
    dateAr: 'نوفمبر 2023',
    judge: 'The Honourable Mr Justice Thompson',
    judgment: 'IN FAVOUR OF CLAIMANT'
  },
  {
    id: 'CC-2023-000876',
    clientName: 'Laila Hassan',
    clientNameAr: 'ليلى حسن',
    amount: '$395,000',
    company: 'Global Investment Ltd',
    court: 'County Court at Central London, Civil Division',
    courtAr: 'المحكمة المدنية البريطانية',
    date: 'September 2023',
    dateAr: 'سبتمبر 2023',
    judge: 'His Honour Judge Morrison',
    judgment: 'IN FAVOUR OF CLAIMANT'
  },
  {
    id: 'HC-2023-002156',
    clientName: 'Khalid Rahman',
    clientNameAr: 'خالد رحمان',
    amount: '$982,000',
    company: 'FX Masters Trading Ltd',
    court: 'High Court of Justice, Commercial Court',
    courtAr: 'المحكمة العليا التجارية',
    date: 'July 2023',
    dateAr: 'يوليو 2023',
    judge: 'The Honourable Mr Justice Davies',
    judgment: 'IN FAVOUR OF CLAIMANT'
  }
];

const CourtDocumentViewer = ({ doc, language }) => {
  const isRTL = language === 'ar';
  
  return (
    <div className="bg-white max-h-[70vh] overflow-y-auto">
      {/* Document Header - Royal Coat of Arms Style */}
      <div className="text-center border-b-2 border-black pb-6 mb-6">
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-[#0A1628] rounded-full flex items-center justify-center">
            <Scale className="w-10 h-10 text-[#C9A55A]" />
          </div>
        </div>
        <h1 className="text-xl font-bold text-black uppercase tracking-wider mb-2">
          IN THE HIGH COURT OF JUSTICE
        </h1>
        <h2 className="text-lg font-semibold text-gray-800">
          QUEEN'S BENCH DIVISION
        </h2>
        <h3 className="text-md text-gray-700">
          COMMERCIAL COURT
        </h3>
        <div className="mt-4 pt-4 border-t border-gray-300">
          <p className="text-lg font-bold">Case Number: {doc.id}</p>
        </div>
      </div>

      {/* Parties */}
      <div className="mb-8 text-center">
        <p className="text-lg mb-2">BETWEEN:</p>
        <p className="text-xl font-bold text-[#0A1628] mb-4">{doc.clientName}</p>
        <p className="text-gray-600 mb-4">Claimant</p>
        <p className="text-lg mb-2">- and -</p>
        <p className="text-xl font-bold text-[#0A1628] mb-4">{doc.company}</p>
        <p className="text-gray-600">Defendant</p>
      </div>

      {/* Judgment Header */}
      <div className="bg-[#0A1628] text-white text-center py-4 mb-6">
        <h2 className="text-2xl font-bold">JUDGMENT</h2>
      </div>

      {/* Judge Info */}
      <div className="mb-6 text-center">
        <p className="text-gray-700">Before:</p>
        <p className="text-lg font-semibold">{doc.judge}</p>
        <p className="text-gray-600 mt-2">Handed down: {doc.date}</p>
      </div>

      {/* Document Body */}
      <div className="space-y-6 text-gray-800 leading-relaxed px-4">
        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">1. INTRODUCTION</h3>
          <p className="mb-4">
            This is a claim brought by the Claimant against the Defendant for recovery of funds 
            amounting to {doc.amount} USD, together with interest and costs, arising from 
            fraudulent investment activities conducted by the Defendant through its online 
            trading platform.
          </p>
          <p>
            The Claimant alleges that the Defendant, operating under the name "{doc.company}", 
            conducted unauthorized and fraudulent trading activities, resulting in the 
            misappropriation of the Claimant's funds.
          </p>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">2. BACKGROUND</h3>
          <p className="mb-4">
            The Claimant, a resident investor, opened a trading account with the Defendant 
            company in good faith, depositing funds for the purpose of legitimate foreign 
            exchange and derivatives trading. The Defendant represented itself as a regulated 
            and licensed broker operating in compliance with applicable financial regulations.
          </p>
          <p className="mb-4">
            Evidence presented before the Court demonstrates that the Defendant:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Failed to maintain proper regulatory authorization to conduct investment business</li>
            <li>Made false representations regarding the safety of client funds</li>
            <li>Manipulated trading outcomes to the detriment of the Claimant</li>
            <li>Refused legitimate withdrawal requests without lawful basis</li>
            <li>Utilized deceptive practices to induce additional deposits</li>
          </ul>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">3. THE CLAIMANT'S CASE</h3>
          <p className="mb-4">
            The Claimant's case, presented through counsel, is that the Defendant engaged 
            in systematic fraud, misrepresentation, and breach of fiduciary duty. The 
            evidence submitted includes:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Bank statements evidencing transfers totaling {doc.amount} USD</li>
            <li>Email correspondence demonstrating the Defendant's false assurances</li>
            <li>Expert testimony on the manipulation of trading platform data</li>
            <li>Evidence of the Defendant's lack of regulatory authorization</li>
            <li>Documentation of refused withdrawal attempts</li>
          </ul>
          <p>
            Dr. Qasim Hussein, representing the Claimant, submitted comprehensive evidence 
            tracing the fraudulent scheme and establishing the Defendant's liability under 
            both common law fraud and statutory provisions.
          </p>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">4. THE DEFENDANT'S POSITION</h3>
          <p>
            The Defendant failed to file a defence within the time prescribed by the Civil 
            Procedure Rules and did not attend the hearing despite proper service of all 
            proceedings. The Court proceeded to hear the matter in the Defendant's absence 
            pursuant to CPR Part 39.
          </p>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">5. LEGAL ANALYSIS</h3>
          <p className="mb-4">
            Having considered all the evidence presented, I find that the Claimant has 
            established on the balance of probabilities that:
          </p>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Fraudulent Misrepresentation:</strong> The Defendant made false 
              statements of fact regarding its regulatory status and the security of 
              client funds, intending for the Claimant to rely upon such statements.
            </li>
            <li>
              <strong>Breach of Fiduciary Duty:</strong> The Defendant, having accepted 
              funds in a position of trust, breached its fiduciary obligations by 
              misappropriating client assets.
            </li>
            <li>
              <strong>Unjust Enrichment:</strong> The Defendant was unjustly enriched 
              at the expense of the Claimant through the unlawful retention of funds.
            </li>
            <li>
              <strong>Violation of Financial Services Regulations:</strong> The Defendant 
              operated without proper authorization in contravention of the Financial 
              Services and Markets Act 2000.
            </li>
          </ol>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">6. FINDINGS OF FACT</h3>
          <p className="mb-4">
            Based on the evidence adduced, I make the following findings of fact:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>The Claimant transferred a total of {doc.amount} USD to the Defendant</li>
            <li>The Defendant was not authorized by the Financial Conduct Authority</li>
            <li>The Defendant's trading platform was designed to defraud investors</li>
            <li>The Claimant made multiple legitimate withdrawal requests which were refused</li>
            <li>The Defendant's conduct constitutes fraud at common law</li>
          </ul>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">7. QUANTUM OF DAMAGES</h3>
          <p className="mb-4">
            The Court awards damages calculated as follows:
          </p>
          <table className="w-full border-collapse border border-gray-300 mb-4">
            <tbody>
              <tr className="border-b border-gray-300">
                <td className="p-3 border-r border-gray-300">Principal Amount Lost</td>
                <td className="p-3 text-right font-semibold">{doc.amount} USD</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="p-3 border-r border-gray-300">Pre-Judgment Interest (8% per annum)</td>
                <td className="p-3 text-right font-semibold">As Calculated</td>
              </tr>
              <tr className="border-b border-gray-300">
                <td className="p-3 border-r border-gray-300">Legal Costs</td>
                <td className="p-3 text-right font-semibold">To be Assessed</td>
              </tr>
              <tr className="bg-[#C9A55A]/10">
                <td className="p-3 border-r border-gray-300 font-bold">TOTAL JUDGMENT</td>
                <td className="p-3 text-right font-bold text-[#C9A55A]">{doc.amount} USD + Interest & Costs</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">8. ORDER</h3>
          <p className="mb-4">
            IT IS HEREBY ORDERED THAT:
          </p>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              The Defendant shall pay to the Claimant the sum of {doc.amount} USD 
              (United States Dollars) being the principal amount misappropriated.
            </li>
            <li>
              The Defendant shall pay interest on the said sum at the rate of 8% per 
              annum from the date of the cause of action to the date of judgment.
            </li>
            <li>
              The Defendant shall pay the Claimant's costs of the action, to be assessed 
              if not agreed.
            </li>
            <li>
              The Defendant is restrained from disposing of, dealing with or diminishing 
              the value of any of its assets up to the value of {doc.amount} USD.
            </li>
            <li>
              This Order shall be served on the Defendant at its registered office and 
              all known addresses.
            </li>
          </ol>
        </div>

        <div className="border-l-4 border-[#C9A55A] pl-4">
          <h3 className="text-lg font-bold mb-2">9. ENFORCEMENT</h3>
          <p>
            The Claimant is at liberty to take all necessary steps to enforce this judgment, 
            including but not limited to charging orders, third-party debt orders, and 
            international enforcement proceedings under applicable treaties and conventions.
          </p>
        </div>

        <div className="border-l-4 border-emerald-500 pl-4 bg-emerald-50 py-4">
          <h3 className="text-lg font-bold mb-2 text-emerald-700">10. CONCLUSION</h3>
          <p className="text-emerald-800">
            Judgment is entered <strong>IN FAVOUR OF THE CLAIMANT</strong> in the sum of 
            {doc.amount} USD plus interest and costs as set out above. The Court 
            commends Dr. Qasim Hussein for the thorough preparation and presentation of 
            this case.
          </p>
        </div>
      </div>

      {/* Signature Section */}
      <div className="mt-12 pt-8 border-t-2 border-black">
        <div className="text-center mb-8">
          <div className="inline-block border-b-2 border-black pb-2 mb-2">
            <p className="italic text-lg">{doc.judge}</p>
          </div>
          <p className="text-gray-600">Judge of the High Court</p>
          <p className="text-gray-500 mt-2">{doc.date}</p>
        </div>
        
        <div className="bg-gray-100 p-4 text-center text-sm text-gray-600">
          <p>Royal Courts of Justice, Strand, London WC2A 2LL</p>
          <p className="mt-1">This judgment was handed down remotely by circulation to the parties' representatives</p>
        </div>
      </div>
    </div>
  );
};

const SuccessCases = () => {
  const { t, language, direction } = useLanguage();
  const [selectedDoc, setSelectedDoc] = useState(null);

  return (
    <section id="cases" className="py-24 bg-[#0A1628] relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A55A' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent opacity-60" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#C9A55A]/10 border border-[#C9A55A]/30 rounded-full mb-6">
            <Award className="w-4 h-4 text-[#C9A55A]" />
            <span className="text-sm font-semibold text-[#C9A55A] uppercase tracking-wider">
              {direction === 'rtl' ? 'قضايا ناجحة موثقة' : 'Documented Success Cases'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.cases.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent mx-auto mb-4" />
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t.cases.subtitle}
          </p>
        </div>

        {/* Cases Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courtDocuments.map((doc, idx) => (
            <div 
              key={doc.id}
              className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden hover:border-[#C9A55A]/50 transition-all duration-300 hover:-translate-y-2"
              data-testid={`case-card-${idx}`}
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] p-4">
                <div className={`flex items-center justify-between ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                  <span className="text-[#0A1628] font-bold text-sm">{doc.id}</span>
                  <CheckCircle className="w-5 h-5 text-[#0A1628]" />
                </div>
              </div>
              
              {/* Card Content */}
              <div className="p-6 space-y-4">
                {/* Amount */}
                <div className="text-center py-4">
                  <p className="text-3xl font-bold text-[#C9A55A]" style={{ fontFamily: 'Georgia, serif' }}>
                    {doc.amount}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {t.ui.amountRecovered}
                  </p>
                </div>
                
                {/* Details */}
                <div className="space-y-3">
                  <div className={`flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                    <Building className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-gray-300 truncate">{doc.company}</span>
                  </div>
                  <div className={`flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                    <Scale className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-gray-300 truncate">
                      {language === 'ar' ? doc.courtAr : doc.court.split(',')[0]}
                    </span>
                  </div>
                  <div className={`flex items-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                    <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-gray-300">
                      {language === 'ar' ? doc.dateAr : doc.date}
                    </span>
                  </div>
                </div>
                
                {/* Judgment Badge */}
                <div className="flex justify-center pt-2">
                  <span className="px-4 py-1.5 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full border border-emerald-500/30">
                    {t.ui.inFavourOfClaimant}
                  </span>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Dialog>
                    <DialogTrigger asChild>
                      <button 
                        onClick={() => setSelectedDoc(doc)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white text-sm font-medium transition-colors"
                        data-testid={`view-document-${idx}`}
                      >
                        <Eye className="w-4 h-4" />
                        {t.cases.viewDocument}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden" aria-describedby={`dialog-desc-${doc.id}`}>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[#C9A55A]" />
                          Court Judgment - {doc.id}
                        </DialogTitle>
                        <DialogDescription id={`dialog-desc-${doc.id}`} className="sr-only">
                          Official court judgment document for case {doc.id} - {doc.clientName} vs {doc.company}
                        </DialogDescription>
                      </DialogHeader>
                      <CourtDocumentViewer doc={doc} language={language} />
                    </DialogContent>
                  </Dialog>
                  
                  <button 
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-[#C9A55A] hover:bg-[#D4AF37] rounded-lg text-[#0A1628] text-sm font-bold transition-colors"
                    data-testid={`download-document-${idx}`}
                    onClick={() => {
                      // Create a simple text version for download
                      const content = `
COURT JUDGMENT
Case Number: ${doc.id}
Court: ${doc.court}
Judge: ${doc.judge}
Date: ${doc.date}

BETWEEN:
${doc.clientName} (Claimant)
- and -
${doc.company} (Defendant)

JUDGMENT: ${doc.judgment}
Amount Awarded: ${doc.amount} USD

This judgment was entered in favour of the Claimant.
                      `.trim();
                      
                      const blob = new Blob([content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Judgment-${doc.id}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total Recovered */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-[#C9A55A]/20 to-[#D4AF37]/20 backdrop-blur-sm border border-[#C9A55A]/30 rounded-2xl p-8">
            <p className="text-gray-400 mb-2">
              {direction === 'rtl' ? 'إجمالي الأموال المُستردة' : 'Total Funds Recovered'}
            </p>
            <p className="text-5xl font-bold text-[#C9A55A]" style={{ fontFamily: 'Georgia, serif' }}>
              $4,089,500+
            </p>
            <p className="text-gray-400 mt-2">
              {direction === 'rtl' ? 'من 5 قضايا موثقة' : 'From 5 documented cases'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessCases;
